import { useState } from "react";
import { ResponsiveContainer, LineChart, Line, YAxis, XAxis, Tooltip, ReferenceLine } from "recharts";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { SourceBadge } from "@/components/SourceBadge";

const FX = {
  GBP: "#60A5FA", NZD: "#34D399", AUD: "#A78BFA", CHF: "#F472B6",
  EUR: "#FBBF24", USD: "#22D3EE", JPY: "#F87171", CAD: "#94A3B8",
};
const CODES = Object.keys(FX);

function fmtPct(v) {
  if (v == null) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}

export function CurrencyVolatility() {
  const { t } = useLang();
  const PERIODS = [["7D", t("c.fx7")], ["30D", t("c.fx30")]];
  const [period, setPeriod] = useState("7D");
  const { status, data, source } = useNovexData(`/novex/currency-volatility?period=${period}`);
  const series = data?.series || [];
  const latest = data?.latest || [];

  return (
    <div className="rounded-lg border border-[#222733] bg-[#0E1118] p-4 sm:p-5" data-testid="currency-volatility">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-slate-400">{t("c.fxTitle")}</h3>
          <SourceBadge source={source} />
        </div>
        <div className="flex rounded-md border border-[#222733] bg-[#11141A] p-0.5">
          {PERIODS.map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} data-testid={`fx-period-${v}`}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${period === v ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {status === "loading" ? (
        <div className="h-56 animate-pulse rounded bg-slate-800/40" />
      ) : !series.length ? (
        <p className="text-sm text-slate-500">{t("c.fxNone")}</p>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="h-56 flex-1">
            <ResponsiveContainer width="99%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} tickFormatter={(d) => d.slice(5)} minTickGap={30} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#475569" }} tickFormatter={(v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}%`} axisLine={false} tickLine={false} width={50} />
                <ReferenceLine y={0} stroke="#334155" strokeDasharray="3 3" />
                <Tooltip
                  contentStyle={{ background: "#0B0E14", border: "1px solid #222733", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#94A3B8" }}
                  formatter={(v, n) => [`${v > 0 ? "+" : ""}${Number(v).toFixed(2)}%`, n]}
                />
                {CODES.map((c) => (
                  <Line key={c} type="monotone" dataKey={c} stroke={FX[c]} strokeWidth={1.6} dot={false} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex shrink-0 flex-col justify-center gap-1.5 sm:w-32">
            {latest.map((it) => (
              <div key={it.code} className="flex items-center justify-between gap-2 rounded px-1.5 py-1" data-testid={`fx-latest-${it.code}`}>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: FX[it.code] }} />
                  <span className="font-mono text-xs font-semibold text-slate-300">{it.code}</span>
                </span>
                <span className="rounded px-1.5 py-0.5 font-mono text-xs font-bold"
                  style={{ background: it.pct >= 0 ? "#10B98122" : "#EF444422", color: it.pct >= 0 ? "#34D399" : "#F87171" }}>
                  {fmtPct(it.pct)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
